import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, KeyRound, Power, IndianRupee } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DEPARTMENTS, inr, fmtDate, genPassword } from "../lib/format";
import { PageHeader, StatusBadge, EmptyState } from "../components/Common";
import { EmployeeForm } from "../components/EmployeeForm";
import { SalaryDialog } from "../components/SalaryDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function Employees() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [form, setForm] = useState({ open: false, employee: null });
  const [ents, setEnts] = useState([]);
  const [ent, setEnt] = useState("all");
  const [salary, setSalary] = useState(null);

  const load = () => api.get("/employees").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/entities").then((r) => setEnts(r.data));
  }, []);
  const entName = (id) => ents.find((x) => x.id === id)?.name;

  const rows = useMemo(() => list.filter((e) =>
    (dept === "all" || e.department === dept) && (ent === "all" || e.entity_id === ent) &&
    [e.name, e.email, e.employee_code, e.designation].join(" ").toLowerCase().includes(q.toLowerCase())), [list, q, dept, ent]);

  const resetPw = async (e) => {
    const pw = genPassword();
    try {
      await api.post(`/employees/${e.id}/reset-password`, { temp_password: pw });
      toast.success(`New temporary password for ${e.name}: ${pw}`, { duration: 12000 });
    } catch (err) { toast.error(formatErr(err)); }
  };

  const toggle = async (e) => {
    try {
      const { data } = await api.post(`/employees/${e.id}/status`);
      toast.success(`${e.name} is now ${data.status}`);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader eyebrow="People" title={isAdmin ? "Employees" : "Team directory"} subtitle={`${rows.length} of ${list.length} people`}
        actions={isAdmin && <Button onClick={() => setForm({ open: true, employee: null })} data-testid="add-employee-button" className="bg-[#E08A1E] hover:bg-[#C87515] text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Add employee</Button>} />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
          <Input placeholder="Search name, email, ID, designation" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="employee-search-input" />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="sm:w-56 bg-white" data-testid="employee-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All departments</SelectItem>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        {ents.length > 1 && (
          <Select value={ent} onValueChange={setEnt}>
            <SelectTrigger className="sm:w-56 bg-white" data-testid="employee-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All entities</SelectItem>{ents.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {!rows.length ? <EmptyState text="No employees match your filters" testid="employees-empty" /> : (
        <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-x-auto">
          <Table data-testid="employees-table">
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>Department</TableHead><TableHead>Joined</TableHead>
              {isAdmin && <><TableHead>CTC</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.employee_code}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#F7EEE2] text-[#C87515] font-bold flex items-center justify-center text-sm shrink-0">{e.name[0]}</div>
                      <div className="min-w-0">
                        <div className="font-semibold">{e.name} {e.role === "admin" && <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-[#0A0807] text-white">HR</span>}</div>
                        <div className="text-xs text-[#6E6E73]">{e.employee_code} · {e.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><div>{e.department || "—"}</div><div className="text-xs text-[#6E6E73]">{e.designation}</div>{entName(e.entity_id) && <div className="text-[11px] text-[#C87515]" data-testid={`employee-entity-${e.employee_code}`}>{entName(e.entity_id)}</div>}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(e.date_of_joining)}</TableCell>
                  {isAdmin && <>
                    <TableCell className="whitespace-nowrap">{inr(e.ctc_annual)}</TableCell>
                    <TableCell><StatusBadge status={e.status} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => setForm({ open: true, employee: e })} data-testid={`edit-employee-${e.employee_code}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" title="Payroll breakup" onClick={() => setSalary(e)} data-testid={`salary-structure-${e.employee_code}`}><IndianRupee className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" title="Reset password" onClick={() => resetPw(e)} data-testid={`reset-password-${e.employee_code}`}><KeyRound className="h-4 w-4" /></Button>
                      {e.id !== user.id && <Button size="icon" variant="ghost" title="Activate / deactivate" onClick={() => toggle(e)} data-testid={`toggle-status-${e.employee_code}`}><Power className="h-4 w-4" /></Button>}
                    </TableCell>
                  </>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <EmployeeForm open={form.open} employee={form.employee} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={load} />
      <SalaryDialog employee={salary} onClose={() => setSalary(null)} />
    </div>
  );
}
